import {
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  MEETING_RULE_NAME,
} from "../../../core/constants/meetingEmailDefaults";

export {
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  MEETING_RULE_NAME,
} from "../../../core/constants/meetingEmailDefaults";

/**
 * Gmail fetch filter payloads shared between the digest wizard and workflow settings.
 *
 * @example
 * import {
 *   buildWizardGmailFiltersPayload,
 *   parseFiltersPayload,
 *   splitCommaValues,
 * } from "./shared/businessFilterPayload";
 */

export const DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE = "email_daily_digest";

export const INVITE_RULE_NAME = "invite_with_ics";

/** Used when building the invite rule from settings-style controls. */
export const SETTINGS_INVITE_SUBJECT_KEYWORDS = "invite, invitation, reschedule";

/** Default subject keywords embedded in the wizard's invite rule row. */
export const WIZARD_INVITE_SUBJECT_KEYWORDS = "invite,invitation,reschedule";

/**
 * Convert a comma-separated string into a normalized list of values.
 *
 * @example
 * splitCommaValues("zoom.us, teams.microsoft.com");
 * // ["zoom.us", "teams.microsoft.com"]
 */
export const splitCommaValues = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

/**
 * Convert a list of values into a display-ready comma-plus-space string.
 *
 * @example
 * joinCommaValues(["meeting", "zoom"]);
 * // "meeting, zoom"
 */
export const joinCommaValues = (values) => (values || []).join(", ");

/**
 * Build filters array for the multi-rule wizard (dynamic gmailRules form state).
 *
 * @example
 * buildWizardGmailFiltersPayload("email_daily_digest", "OR", []);
 * // []
 */
export const buildWizardGmailFiltersPayload = (
  workflowType,
  filterOperator,
  gmailRules
) => {
  if (workflowType !== DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE) {
    return [];
  }

  const rules = (gmailRules || [])
    .filter((rule) => rule.enabled !== false)
    .map((rule) => ({
      name: (rule.name || "").trim(),
      enabled: rule.enabled !== false,
      sender_patterns: (rule.senderPatterns || "")
        .split(",")
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean),
      subject_keywords: (rule.subjectKeywords || "")
        .split(",")
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean),
      require_ics_attachment: Boolean(rule.requireIcsAttachment),
    }))
    .filter(
      (rule) =>
        rule.sender_patterns.length > 0 || rule.subject_keywords.length > 0
    );

  if (rules.length === 0) {
    return [];
  }

  return [
    {
      type: "gmail_fetch_filter",
      modes: ["rule_based"],
      config: {
        operator: filterOperator === "AND" ? "AND" : "OR",
        rules,
      },
    },
  ];
};

/**
 * Build filters array from simplified settings controls (meeting + invite toggles).
 *
 * @example
 * buildSettingsGmailFiltersPayload({
 *   includeInviteEmails: true,
 *   meetingSenderPatterns: "zoom.us",
 *   meetingSubjectKeywords: "meeting",
 * });
 */
export const buildSettingsGmailFiltersPayload = ({
  includeInviteEmails,
  meetingSenderPatterns,
  meetingSubjectKeywords,
}) => {
  const rules = [];

  const senderPatterns = splitCommaValues(meetingSenderPatterns).map((value) =>
    value.toLowerCase()
  );
  const subjectKeywords = splitCommaValues(meetingSubjectKeywords).map((value) =>
    value.toLowerCase()
  );

  if (senderPatterns.length > 0 || subjectKeywords.length > 0) {
    rules.push({
      name: MEETING_RULE_NAME,
      enabled: true,
      sender_patterns: senderPatterns,
      subject_keywords: subjectKeywords,
      require_ics_attachment: false,
    });
  }

  if (includeInviteEmails) {
    rules.push({
      name: INVITE_RULE_NAME,
      enabled: true,
      sender_patterns: [],
      subject_keywords: splitCommaValues(SETTINGS_INVITE_SUBJECT_KEYWORDS).map(
        (value) => value.toLowerCase()
      ),
      require_ics_attachment: true,
    });
  }

  if (rules.length === 0) {
    return [];
  }

  return [
    {
      type: "gmail_fetch_filter",
      modes: ["rule_based"],
      config: {
        operator: "OR",
        rules,
      },
    },
  ];
};

/**
 * Parse meeting/invite settings from backend filters payload.
 *
 * @example
 * parseFiltersPayload([]);
 * // { includeInviteEmails: true, meetingSenderPatterns: "...", ... }
 */
export const parseFiltersPayload = (filters) => {
  const defaultState = {
    includeInviteEmails: true,
    meetingSenderPatterns: MEETING_DEFAULT_SENDERS,
    meetingSubjectKeywords: MEETING_DEFAULT_KEYWORDS,
  };

  if (!Array.isArray(filters) || filters.length === 0) {
    return defaultState;
  }

  const firstRuleFilter = filters.find(
    (item) =>
      item?.type === "gmail_fetch_filter" && Array.isArray(item?.config?.rules)
  );
  if (!firstRuleFilter) {
    return defaultState;
  }

  const rules = firstRuleFilter.config.rules;
  const inviteRule = rules.find(
    (rule) => rule?.name === INVITE_RULE_NAME || rule?.require_ics_attachment
  );
  const meetingRule =
    rules.find((rule) => rule?.name === MEETING_RULE_NAME) ||
    rules.find(
      (rule) =>
        rule?.name !== INVITE_RULE_NAME &&
        ((Array.isArray(rule?.sender_patterns) && rule.sender_patterns.length > 0) ||
          (Array.isArray(rule?.subject_keywords) && rule.subject_keywords.length > 0))
    );

  return {
    includeInviteEmails: inviteRule ? inviteRule.enabled !== false : false,
    meetingSenderPatterns: joinCommaValues(meetingRule?.sender_patterns || []),
    meetingSubjectKeywords: joinCommaValues(meetingRule?.subject_keywords || []),
  };
};
